// LeadAgentUnlockOffer.service.ts owns the HTTP call for the Lead Agent
// trial-cap unlock offer (RET-1633 / U9). The endpoint is a bff-web proxy of
// leadbilling's server-computed offer (U7/U8): phase, options, recommended tier
// and per-tier prices — never raw token counts. The provider fetches it only
// when the usage payload says the workspace is in the trial-cap cohort
// (`isTrialCap`), so this is a cheap, targeted read.

import { AxiosService } from 'src/axios/axios.service';
import {
  parseLeadAgentUnlockOffer,
  parseLeadAgentUnlockResult,
} from './LeadAgentAcceptance.functions';
import type {
  tLeadAgentAcceptanceEnvelope,
  tLeadAgentUnlockOffer,
  tLeadAgentUnlockResult,
  tUnlockLeadAgentOptions,
} from './LeadAgentAcceptance.types';

export const LeadAgentUnlockOfferService = Object.freeze({
  /**
   * GET /workspace/{wid}/lead-agent/unlock-offer
   *
   * Returns the parsed offer, or null when there is no offer / any error (fail
   * closed: the existing base usage banner keeps rendering and the trial-cap
   * variants stay hidden). A non-2xx rejects inside AxiosService; the provider's
   * catch turns it into null. A malformed 2xx body parses to null too. An
   * ineligible workspace parses to a normalised `{ eligible: false }` offer,
   * which the provider treats the same as "no trial-cap banner".
   */
  getOffer: async function (
    workspaceId: string,
  ): Promise<tLeadAgentUnlockOffer | null> {
    const response = await AxiosService.get<tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/unlock-offer`,
      { isBFFService: true },
    );

    if (!response?.success || !response.data) return null;
    return parseLeadAgentUnlockOffer(response.data);
  },

  /**
   * POST /workspace/{wid}/lead-agent/unlock  (RET-1633 / U10)
   *
   * Body `{ tier }` — no `prorationDate` echo (the offer already carries the
   * exact amount, and the backend mints a FRESH Stripe idempotency key per
   * user-initiated retry, so a retry re-POSTs the SAME body verbatim). A 200 may
   * still carry `status: 'failed'` (a payment decline — retryable) or
   * `status: 'requires_action'` (3DS, confirmed client-side); the thrown bands
   * reach the modal intact — 409 `in_progress` (retryable) / `already_extended`
   * (blocked), other 4xx (blocked), 424/5xx (retryable). An unparseable 2xx body
   * throws so the modal never fabricates a success from a drifted payload.
   */
  performUnlock: async function (
    workspaceId: string,
    options: tUnlockLeadAgentOptions,
  ): Promise<tLeadAgentUnlockResult> {
    const response = await AxiosService.post<
      tUnlockLeadAgentOptions,
      tLeadAgentAcceptanceEnvelope
    >(
      `workspace/${workspaceId}/lead-agent/unlock`,
      { tier: options.tier },
      { isBFFService: true },
    );

    const result = parseLeadAgentUnlockResult(response?.data);
    if (!result) {
      throw new Error('lead-agent/unlock returned an unparseable payload');
    }
    return result;
  },
});
